import React, { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Building2, Users, Tags, Plus, Trash2, Loader2, Pencil, Check, X, Upload } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const SECTIONS = {
  companies: { title: "Firmen", icon: Building2, ep: "/companies", fields: [["name", "Firmenname *"], ["contact_person", "Ansprechpartner"], ["phone", "Telefon"], ["email", "E-Mail"]] },
  employees: { title: "Mitarbeiter", icon: Users, ep: "/employees", fields: [["name", "Name *"], ["role", "Funktion"], ["company", "Firma"], ["phone", "Telefon"]] },
  categories: { title: "Kategorien", icon: Tags, ep: "/categories", fields: [["name", "Bezeichnung *"]] },
};

function Section({ cfg }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const load = () => api.get(cfg.ep).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, [cfg.ep]); // eslint-disable-line react-hooks/exhaustive-deps

  const add = async (e) => {
    e.preventDefault();
    if (!form.name?.trim()) return toast.error("Name ist erforderlich");
    setBusy(true);
    try { await api.post(cfg.ep, form); setForm({}); load(); toast.success("Gespeichert"); }
    catch { toast.error("Speichern fehlgeschlagen"); } finally { setBusy(false); }
  };
  const del = async (id) => { await api.delete(`${cfg.ep}/${id}`); load(); };
  const importRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const doImport = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post(`${cfg.ep}/import`, fd);
      toast.success(`${data.inserted} importiert${data.skipped ? `, ${data.skipped} übersprungen` : ""}`);
      load();
    } catch { toast.error("Import fehlgeschlagen"); } finally { setImporting(false); }
  };
  const startEdit = (it) => { setEditId(it.id); setEditForm({ ...it }); };
  const cancelEdit = () => { setEditId(null); setEditForm({}); };
  const saveEdit = async () => {
    if (!editForm.name?.trim()) return toast.error("Name ist erforderlich");
    const payload = {};
    cfg.fields.forEach(([k]) => { payload[k] = editForm[k] || ""; });
    try { await api.put(`${cfg.ep}/${editId}`, payload); cancelEdit(); load(); toast.success("Aktualisiert"); }
    catch { toast.error("Aktualisieren fehlgeschlagen"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-xs text-slate-500">Import-Spalten: {cfg.fields.map((f) => f[1].replace(" *", "")).join(", ")}</p>
        <Button type="button" size="sm" variant="outline" disabled={importing} onClick={() => importRef.current?.click()} data-testid="stamm-import-btn">
          {importing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}CSV/Excel importieren
        </Button>
        <input ref={importRef} type="file" accept=".csv,.xlsx,.xlsm" hidden onChange={doImport} data-testid="stamm-import-input" />
      </div>
      <form onSubmit={add} className="grid grid-cols-1 sm:grid-cols-2 gap-2 bg-white rounded-xl border border-slate-200 p-3" data-testid={`stamm-form${cfg.ep}`}>
        {cfg.fields.map(([k, l]) => (
          <Input key={k} placeholder={l} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`stamm-${k}-input`} />
        ))}
        <Button type="submit" disabled={busy} className="sm:col-span-2" data-testid="stamm-add-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}Hinzufügen
        </Button>
      </form>
      <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
        {items.length === 0 && <div className="p-6 text-center text-sm text-slate-400">Noch keine Einträge.</div>}
        {items.map((it) => (
          editId === it.id ? (
            <div key={it.id} className="p-3 space-y-2" data-testid={`stamm-edit-${it.id}`}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {cfg.fields.map(([k, l]) => (
                  <Input key={k} placeholder={l} value={editForm[k] || ""} onChange={(e) => setEditForm({ ...editForm, [k]: e.target.value })} data-testid={`stamm-edit-${k}-input`} />
                ))}
              </div>
              <div className="flex gap-2 justify-end">
                <Button size="sm" variant="ghost" onClick={cancelEdit} data-testid={`stamm-edit-cancel-${it.id}`}><X className="h-4 w-4 mr-1" />Abbrechen</Button>
                <Button size="sm" onClick={saveEdit} data-testid={`stamm-edit-save-${it.id}`}><Check className="h-4 w-4 mr-1" />Speichern</Button>
              </div>
            </div>
          ) : (
            <div key={it.id} className="flex items-center gap-3 p-3" data-testid={`stamm-item-${it.id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-slate-900 truncate">{it.name}</div>
                <div className="text-xs text-slate-500 truncate">{[it.contact_person, it.role, it.company, it.phone, it.email].filter(Boolean).join(" · ")}</div>
              </div>
              <button onClick={() => startEdit(it)} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500" data-testid={`stamm-edit-btn-${it.id}`}><Pencil className="h-4 w-4" /></button>
              <button onClick={() => del(it.id)} className="p-2 rounded-lg hover:bg-red-50 text-red-500" data-testid={`stamm-del-${it.id}`}><Trash2 className="h-4 w-4" /></button>
            </div>
          )
        ))}
      </div>
    </div>
  );
}

export default function Stammdaten() {
  return (
    <div className="fade-up max-w-3xl" data-testid="stammdaten-page">
      <h1 className="text-2xl font-head font-extrabold text-slate-900 mb-1">Stammdaten</h1>
      <p className="text-slate-500 text-sm mb-6">Firmen, Mitarbeiter und Kategorien zentral verwalten – als Auswahl in Mängeln & Tickets.</p>
      <Tabs defaultValue="companies">
        <TabsList data-testid="stamm-tabs">
          {Object.entries(SECTIONS).map(([k, c]) => (
            <TabsTrigger key={k} value={k} data-testid={`stamm-tab-${k}`}><c.icon className="h-4 w-4 mr-1.5" />{c.title}</TabsTrigger>
          ))}
        </TabsList>
        {Object.entries(SECTIONS).map(([k, c]) => (
          <TabsContent key={k} value={k} className="mt-4"><Section cfg={c} /></TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
